import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import './Admin.css';

interface Claim {
  id: number;
  warranty_id: number;
  user_id: number;
  product_image: string;
  video_proof: string;
  upi_id: string;
  upi_qr: string;
  status: string;
  admin_notes: string;
  created_at: string;
  serial_number: string;
  order_id: string;
  user_mobile: string;
}

const AdminClaims: React.FC = () => {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');

  useEffect(() => {
    const status = searchParams.get('status') || 'all';
    fetchClaims(status === 'all' ? null : status);
  }, [searchParams]);

  const fetchClaims = async (status: string | null = null) => {
    try {
      const params: any = status ? { status } : {};
      if (search) params.search = search;
      const response = await axios.get('/api/admin/claims', { params });
      setClaims(response.data.claims);
    } catch (error) {
      console.error('Failed to fetch claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    if (!window.confirm('Are you sure you want to approve this claim? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.post(`/api/admin/claims/${id}/approve`, { notes });
      setSelectedClaim(null);
      setNotes('');
      fetchClaims();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to approve claim');
    }
  };

  const handleReject = async (id: number) => {
    if (!window.confirm('Are you sure you want to reject this claim? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.post(`/api/admin/claims/${id}/reject`, { notes });
      setSelectedClaim(null);
      setNotes('');
      fetchClaims();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to reject claim');
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Loading...</div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container">
        <h1 className="page-title">📋 Claim Management</h1>
        <div className="filters">
          <button
            className="btn btn-secondary"
            onClick={() => fetchClaims()}
          >
            All
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => fetchClaims('pending')}
          >
            Pending
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => fetchClaims('approved')}
          >
            Approved
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => fetchClaims('rejected')}
          >
            Rejected
          </button>
        </div>
        <div style={{ marginBottom: '20px', maxWidth: '400px', display: 'flex', gap: '10px' }}>
          <input
            type="text"
            placeholder="Search Order ID"
            className="form-control"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchClaims()}
            style={{ flex: 1 }}
          />
          <button
            className="btn btn-primary"
            onClick={() => fetchClaims()}
            style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            <span>🔍</span> Search
          </button>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Order ID</th>
                <th>User Mobile</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((claim) => (
                <tr key={claim.id}>
                  <td>{claim.id}</td>
                  <td>{claim.order_id}</td>
                  <td>{claim.user_mobile}</td>
                  <td>
                    <span className={`status-badge status-${claim.status}`}>
                      {claim.status}
                    </span>
                  </td>
                  <td>{new Date(claim.created_at).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn btn-primary"
                      onClick={() => setSelectedClaim(claim)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedClaim && (
          <div className="modal-overlay" onClick={() => setSelectedClaim(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Claim Details #{selectedClaim.id}</h2>
              <div className="claim-details">
                <p><strong>Order ID:</strong> {selectedClaim.order_id}</p>
                <p><strong>Serial Number:</strong> {selectedClaim.serial_number}</p>
                <p><strong>User Mobile:</strong> {selectedClaim.user_mobile}</p>
                <p><strong>Status:</strong> {selectedClaim.status}</p>
                <p><strong>Submitted:</strong> {new Date(selectedClaim.created_at).toLocaleString()}</p>
                {selectedClaim.product_image && (
                  <div>
                    <h4>Product Image</h4>
                    <img
                      src={selectedClaim.product_image}
                      alt="Product"
                      className="claim-image"
                    />
                  </div>
                )}
                {selectedClaim.video_proof && (
                  <div>
                    <h4>Video Proof</h4>
                    <video
                      src={selectedClaim.video_proof}
                      controls
                      className="claim-video"
                    />
                  </div>
                )}
                {selectedClaim.upi_qr && (
                  <div>
                    <h4>UPI QR Code</h4>
                    <img
                      src={selectedClaim.upi_qr}
                      alt="UPI QR"
                      className="claim-image"
                    />
                  </div>
                )}
                {selectedClaim.upi_id && (
                  <p><strong>UPI ID:</strong> {selectedClaim.upi_id}</p>
                )}
                {selectedClaim.status === 'pending' && (
                  <>
                    <div className="form-group">
                      <label>Admin Notes (Optional)</label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="modal-actions">
                      <button
                        className="btn btn-success"
                        onClick={() => handleApprove(selectedClaim.id)}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleReject(selectedClaim.id)}
                      >
                        Reject
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedClaim(null)}
                style={{ marginTop: '20px' }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminClaims;

